import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { usePlaySound, btnDelay, useScale } from "common/utils";
import { ParentGate, ParentWarningTitle } from "features/parent";

export const AuthSelection = ({ open, close }: { open: boolean; close: () => void }) => {
  const PANEL_WIDTH = 900;
  const PANEL_HEIGHT = 700;
  const play = usePlaySound();
  const { scale } = useScale();
  const navigate = useNavigate();
  const [signIn, setSignIn] = useState(false);
  const [parentGate, setParentGate] = useState(false);

  const handleClose = () => {
    btnDelay(() => {
      play();
      close();
    });
  };

  const handleCloseParentGate = () => {
    setParentGate(false);
  };

  const handleConfirm = () => {
    setParentGate(false);
    if (signIn) {
      navigate("/auth/login");
    } else {
      navigate("/auth/register");
    }
  };

  const handleSignIn = () => {
    close();
    btnDelay(() => {
      play();
      setSignIn(true);
      setParentGate(true);
    });
  };

  const handleSignUp = () => {
    close();
    btnDelay(() => {
      play();
      setSignIn(false);
      setParentGate(true);
    });
  };

  return (
    <>
      <Dialog
        fullScreen
        open={open}
        maxWidth={false}
        sx={{ zIndex: 999999 }}
        TransitionComponent={FadeTransition}
        PaperProps={{
          style: {
            margin: 0,
            overflow: "visible",
            backgroundColor: "transparent",
          },
          elevation: 0,
        }}
      >
        <div className="flex-col-el flex-center absolute inset-0 bg-textcolor-black/50 w-full h-full">
          <div className="flex-col-el flex-center relative w-full h-full">
            <div
              className="flex-col-view self-center"
              style={{
                width: PANEL_WIDTH * scale,
                height: PANEL_HEIGHT * scale,
              }}
            >
              <div
                className="flex-col-view origin-top-left"
                style={{
                  width: PANEL_WIDTH,
                  height: PANEL_HEIGHT,
                  transform: `scale(${scale})`,
                }}
              >
                <CloseBtn close={handleClose} />

                <div className="flex-col-el flex-center w-full h-full rounded-[8px] bg-beige-60 p-6 shadow-[0_12px_40px_rgba(63,74,97,0.12),0_4px_12px_rgba(63,74,97,0.08)] border-[8px] border-solid border-white relative overflow-hidden">
                  <ParentWarningTitle />

                  <div className="w-full max-w-[800px] mx-auto mb-5">
                    <p className="text-body text-lg text-textcolor-black text-center leading-relaxed px-4">
                      {I18n.t("MSG_AUTH_SELECTION_INSTRUCTION")}
                    </p>
                  </div>

                  <div className="flex-col-center w-full relative z-10">
                    <div className="flex-row-center gap-10 w-full flex-1 max-w-2xl mx-auto">
                      <div className="flex-col-el upsilon flex-1">
                        <button
                          onClick={handleSignIn}
                          className="btn btn-primary !py-4 w-full rounded-2xl cursor-pointer flex-col-center text-2xl min-h-[200px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-xl relative overflow-hidden group"
                        >
                          <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <div className="flex-col-center items-center justify-center h-full relative z-10">
                            <div className="w-24 h-24 bg-white/20 rounded-3xl flex items-center justify-center mb-4 shadow-lg backdrop-blur-sm border border-white/10 transition-all duration-300 group-hover:bg-white/30 group-hover:shadow-xl group-hover:scale-110">
                              <span className="material-symbols-outlined !text-6xl text-white transition-transform duration-300 group-hover:rotate-12">
                                login
                              </span>
                            </div>
                            <div className="text-center">
                              <p className="text-body text-3xl mb-2 transition-all duration-300 group-hover:text-white">
                                {I18n.t("MSG_AUTH_SIGN_IN")}
                              </p>
                              <p className="text-body text-base opacity-90 leading-relaxed transition-all duration-300 group-hover:opacity-100 !mt-4">
                                {I18n.t("MSG_AUTH_SIGN_IN_DESC")}
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>

                      <div className="flex-col-el upsilon flex-1">
                        <button
                          onClick={handleSignUp}
                          className="btn btn-primary-outlined !py-4 w-full rounded-2xl cursor-pointer flex-col-center text-2xl min-h-[200px] transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] hover:shadow-xl relative overflow-hidden group"
                        >
                          <div className="absolute inset-0 bg-brand-color/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          <div className="flex-col-center items-center justify-center h-full relative z-10">
                            <div className="w-24 h-24 bg-brand-color/10 rounded-3xl flex items-center justify-center mb-4 shadow-lg backdrop-blur-sm border border-brand-color/20 transition-all duration-300 group-hover:bg-brand-color/20 group-hover:shadow-xl group-hover:scale-110">
                              <span className="material-symbols-outlined !text-6xl text-brand-color transition-transform duration-300 group-hover:rotate-12">
                                person_add
                              </span>
                            </div>
                            <div className="text-center">
                              <p className="text-body text-3xl mb-2 text-brand-color transition-all duration-300">
                                {I18n.t("MSG_AUTH_SIGN_UP")}
                              </p>
                              <p className="text-body text-base text-brand-color opacity-75 leading-relaxed transition-all duration-300 group-hover:opacity-90 !mt-4">
                                {I18n.t("MSG_AUTH_SIGN_UP_DESC")}
                              </p>
                            </div>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      <ParentGate visible={parentGate} close={handleCloseParentGate} confirm={handleConfirm} />
    </>
  );
};
